import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import React from 'react';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  Box,
  CssBaseline,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryIcon from '@mui/icons-material/History';
import LoginPage from '../pages/LoginPage';
import PrivateRoute from './PrivateRoute';
import SubscriptionRoute from './SubscriptionRoute';
import Profile from '../pages/Profile';
import Payee from '../pages/Payee';
import CreatePayee from '../pages/CreatePayee';
import TimesheetForm from '../pages/TimesheetForm';
import Payments from '../pages/Payments';
import Users from '../pages/Users';
import Subscriptions from '../pages/Subscriptions';
import SubscriptionManagement from '../pages/SubscriptionManagement';
import PaymentSettingsPage from '../pages/PaymentSettingsPage';
import Dashboard from '../pages/Dashboard';
import Reports from '../pages/Reports';
import Team from '../pages/Team';
import AcceptInvite from '../pages/AcceptInvite';
import AuditLogs from '../pages/AuditLogs';
import Form1099Records from '../pages/Form1099Records';
import AdminRoute from '../auth/AdminRoute';
import ResetPassword from '../pages/ResetPassword';
import RegisterPage from '../pages/RegisterPage';
import RegistrationSuccess from '../pages/RegistrationSuccess';
import { useAuth } from '../auth/AuthContext';
import Sidebar, { drawerWidth } from './Sidebar';
import TourComponent from './Tour';

// Redirect to marketing site for registration-related routes
const MARKETING_URL =
  process.env.REACT_APP_MARKETING_URL || 'http://localhost:6003';

function RedirectToMarketing({ path }: { path: string }) {
  React.useEffect(() => {
    window.location.href = `${MARKETING_URL}${path}`;
  }, [path]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasActiveSubscription, userRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // hide sidebar on auth pages or when there's no token (not logged in)
  const hideSidebar =
    ['/login', '/register', '/registration', '/registration/success', '/reset-password', '/accept-invite'].includes(
      location.pathname
    ) || location.pathname.startsWith('/register') || !localStorage.getItem('token');

  const [openLogoutDialog, setOpenLogoutDialog] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Determine if user should have limited access (Client without active subscription)
  const hasLimitedAccess = userRole === 'Client' && !hasActiveSubscription;

  // Build navigation based on role and subscription status
  const navigation = React.useMemo(() => {
    // If Client without active subscription, only show My Subscription
    if (hasLimitedAccess) {
      return [
        {
          label: 'My Subscription',
          path: '/my-subscription',
          icon: <ReceiptLongIcon />,
        },
      ];
    }

    // Full navigation for admins or clients with active subscription
    return [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: <DashboardIcon />,
      },
      { label: 'Payees', path: '/payees', icon: <PeopleIcon /> },
      { label: 'Timesheets', path: '/timesheet', icon: <ScheduleIcon /> },
      {
        label: 'Tax Information',
        path: '/taxes',
        icon: <PaymentIcon />,
      },
      {
        label: '1099 Records',
        path: '/form1099-records',
        icon: <ReceiptLongIcon />,
      },
      { label: 'Reports', path: '/reports', icon: <AssessmentIcon /> },
      {
        label: 'Profile',
        path: '/profile',
        icon: <AccountCircleIcon />,
      },
      { label: 'Team', path: '/team', icon: <GroupsIcon /> },
      ...(userRole?.toLowerCase() === 'admin'
        ? [
            { label: 'Manage Users', path: '/users', icon: <PeopleIcon /> },
            {
              label: 'Subscriptions',
              path: '/subscriptions',
              icon: <ReceiptLongIcon />,
            },
            {
              label: 'Payment Settings',
              path: '/payment-settings',
              icon: <SettingsIcon />,
            },
            {
              label: 'Audit Logs',
              path: '/audit-logs',
              icon: <HistoryIcon />,
            },
          ]
        : [
            {
              label: 'My Subscription',
              path: '/my-subscription',
              icon: <ReceiptLongIcon />,
            },
            {
              label: 'Activity Log',
              path: '/audit-logs',
              icon: <HistoryIcon />,
            },
          ]),
    ];
  }, [userRole, hasLimitedAccess]);

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    localStorage.clear();
    setOpenLogoutDialog(false);
    window.dispatchEvent(new Event('authChanged'));
    navigate('/login', { replace: true });
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <CssBaseline />

      {/* Mobile AppBar */}
      {isMobile && !hideSidebar && (
        <AppBar
          position="fixed"
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src="/logo192.png"
              alt="SimplyPayroll"
              sx={{ width: 32, height: 32, mr: 1.5 }}
            />
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
              SimplyPayroll
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {!hideSidebar && (
        <Sidebar
          navigation={navigation}
          userRole={userRole}
          onLogout={handleLogoutClick}
          isMobile={isMobile}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
      )}

      {/* Logout Confirmation Dialog */}
      <Dialog open={openLogoutDialog} onClose={handleLogoutCancel}>
        <DialogTitle>Confirm Sign Out</DialogTitle>
        <DialogContent>Are you sure you want to sign out?</DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>Cancel</Button>
          <Button
            onClick={handleLogoutConfirm}
            color="primary"
            variant="contained"
          >
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: hideSidebar || isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
          minHeight: '100vh',
          mt: isMobile && !hideSidebar ? '64px' : 0,
        }}
      >
        <Routes>
          <Route
            path="/"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <Dashboard />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <Dashboard />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/payees"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <Payee />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <Profile />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/users"
            element={
              <AdminRoute>
                <Users />
              </AdminRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <AdminRoute>
                <Subscriptions />
              </AdminRoute>
            }
          />
          <Route
            path="/payment-settings"
            element={
              <AdminRoute>
                <PaymentSettingsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <AuditLogs />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/form1099-records"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <Form1099Records />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/taxes"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <Payments />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <Reports />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/team"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <Team />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          {/* Registration routes */}
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/registration" element={<RegisterPage />} />
          <Route path="/registration/success" element={<RegistrationSuccess />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/createPayee"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <CreatePayee />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/editPayee"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <CreatePayee />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/timesheet"
            element={
              <PrivateRoute>
                <SubscriptionRoute>
                  <TimesheetForm />
                </SubscriptionRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/my-subscription"
            element={
              <PrivateRoute>
                <SubscriptionManagement />
              </PrivateRoute>
            }
          />
          {/* Legacy route - redirect to new path */}
          <Route
            path="/manage-subscription"
            element={
              <PrivateRoute>
                <SubscriptionManagement />
              </PrivateRoute>
            }
          />
        </Routes>
      </Box>

      {/* Tour Component */}
      <TourComponent />
    </Box>
  );
}

export default Layout;
